
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { usePasswordRecoverySession } from "@/hooks/usePasswordRecoverySession";
import { ResetPasswordForm } from "@/components/auth/ResetPasswordForm";
import { SessionStatusAlert } from "@/components/auth/SessionStatusAlert";
import { LoadingCard } from "@/components/auth/LoadingCard";
import { AlertTriangle, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const { isValidSession, sessionError } = usePasswordRecoverySession();

  // Show loading state while checking session
  if (isValidSession === null) {
    return <LoadingCard />;
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
      <div className="w-full max-w-md p-4">
        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">Reset Password</CardTitle>
            <CardDescription className="text-center">
              {isValidSession 
                ? "Enter your new password below" 
                : "Password reset session verification"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sessionError && !isValidSession && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-red-800 mb-1">
                      Session Error
                    </h3>
                    <p className="text-sm text-red-700">
                      {sessionError}
                    </p>
                    <div className="mt-3 text-sm text-red-600">
                      <p>To reset your password:</p>
                      <ol className="list-decimal list-inside mt-1 space-y-1">
                        <li>Go to the login page</li>
                        <li>Click "Forgot Password?"</li>
                        <li>Enter your email address</li>
                        <li>Check your email for the reset link</li>
                        <li>Click the link in the email</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <SessionStatusAlert 
              isValidSession={isValidSession} 
              sessionError={sessionError} 
            />
            
            <ResetPasswordForm isValidSession={isValidSession} />
            
            <div className="mt-6 space-y-3">
              <Button 
                variant="outline" 
                className="w-full" 
                onClick={() => navigate("/login")}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
              
              {!isValidSession && (
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-2">
                    Need to request a new reset link?
                  </p>
                  <Button 
                    variant="link" 
                    onClick={() => navigate("/login")}
                    className="text-sm"
                  >
                    Go to Login Page
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
